import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { DailyCommuteLocation } from "@/types/dailyCommuteLocation";
import type { CalculateSeatFareResponse } from "@/types/fareCalculation";
import {
  buildFareCalculationRequest,
  calculateSeatFare,
  prewarmFareEngine,
} from "@/services/fareEngineService";
import { resolveLocationFromCoordinates } from "@/services/googlePlacesRestService";
import { getBrowserLocation } from "@/utils/geoLocationUtils";
import { useRouteEstimate } from "@/hooks/useRouteEstimate";
import { GUEST_DEFAULT_VEHICLE } from "@/utils/commuteFareComparison";

/**
 * Minimal commute calculator for unauthenticated guests: the visitor only enters a
 * From/To route. The vehicle profile is fixed (see GUEST_DEFAULT_VEHICLE), and we use
 * the fare engine's suggested per-seat fare to compare against solo cab/auto costs.
 */
export function useGuestCommuteCalculator() {
  const [fromPlace, setFromPlace] = useState<DailyCommuteLocation | null>(null);
  const [toPlace, setToPlace] = useState<DailyCommuteLocation | null>(null);
  const [destinationKey, setDestinationKey] = useState(0);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [fareResult, setFareResult] = useState<CalculateSeatFareResponse | null>(null);

  const hasAutoDetectedRef = useRef(false);

  // Detect the starting location once on mount. Never re-runs (e.g. after the user
  // clears a field). The destination is left empty for the user to enter.
  useEffect(() => {
    prewarmFareEngine();
    if (hasAutoDetectedRef.current) return;

    hasAutoDetectedRef.current = true;
    let cancelled = false;

    const detect = async () => {
      setIsDetectingLocation(true);

      let coords: { lat: number; lng: number };
      try {
        coords = await getBrowserLocation({ timeout: 8000, maximumAge: 60_000 });
      } catch {
        if (!cancelled) setIsDetectingLocation(false);
        return;
      }

      try {
        const detectedOrigin = await resolveLocationFromCoordinates(
          coords.lat,
          coords.lng
        );
        if (!cancelled) setFromPlace(detectedOrigin);
      } catch {
        // Reverse geocoding failed — user can search manually
      } finally {
        if (!cancelled) setIsDetectingLocation(false);
      }
    };

    void detect();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasRoute = Boolean(fromPlace && toPlace);
  const {
    data: routeEstimate,
    isLoading: routeEstimateLoading,
    isError: routeEstimateError,
  } = useRouteEstimate(fromPlace, toPlace, hasRoute);

  const fareMutation = useMutation({
    mutationFn: calculateSeatFare,
    onSuccess: (response) => {
      setFareResult(response);
      setValidationError(null);
    },
    onError: (error: Error) => {
      setFareResult(null);
      setValidationError(error.message);
    },
  });

  const clearValidationError = useCallback(() => setValidationError(null), []);

  const clearResults = useCallback(() => {
    setFareResult(null);
    setValidationError(null);
  }, []);

  const handleFromSelect = useCallback(
    (place: DailyCommuteLocation) => {
      clearResults();
      setFromPlace((previous) => {
        if (
          previous?.countryCode &&
          place.countryCode &&
          previous.countryCode !== place.countryCode
        ) {
          setToPlace(null);
          setDestinationKey((key) => key + 1);
        }
        return place;
      });
    },
    [clearResults]
  );

  const handleToSelect = useCallback(
    (place: DailyCommuteLocation) => {
      clearResults();
      setToPlace(place);
    },
    [clearResults]
  );

  const handleClearFrom = useCallback(() => {
    clearResults();
    setFromPlace(null);
  }, [clearResults]);

  const handleClearTo = useCallback(() => {
    clearResults();
    setToPlace(null);
    setDestinationKey((key) => key + 1);
  }, [clearResults]);

  const handleCalculate = useCallback(() => {
    if (!fromPlace?.address?.trim()) {
      setValidationError("Please select your starting location.");
      return;
    }
    if (!toPlace?.address?.trim()) {
      setValidationError("Please select your destination.");
      return;
    }

    setValidationError(null);
    fareMutation.mutate(
      buildFareCalculationRequest({
        origin: fromPlace.address,
        destination: toPlace.address,
        availableSeats: GUEST_DEFAULT_VEHICLE.availableSeats,
        carCompany: GUEST_DEFAULT_VEHICLE.carCompany,
        carModel: GUEST_DEFAULT_VEHICLE.carModel,
        fuelType: GUEST_DEFAULT_VEHICLE.fuelType,
      })
    );
  }, [fromPlace, toPlace, fareMutation]);

  return {
    fromPlace,
    toPlace,
    destinationKey,
    isDetectingLocation,
    validationError,
    fareResult,
    showResults: Boolean(fareResult),
    routeEstimate,
    routeEstimateLoading,
    routeEstimateError,
    isCalculating: fareMutation.isPending,
    clearValidationError,
    handleFromSelect,
    handleToSelect,
    handleClearFrom,
    handleClearTo,
    handleCalculate,
  };
}
